import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useGetUserInfo } from "../Profile/api";
import CardElement from "./CardElement";
import QuickLinks from "./QuickLinks";
import VulnerableCompaniesTable from "./VulnerableCompaniesTable";
import { useVulnerableCompaniesReport } from "./useThreatIntelligence";
import axios from "axios";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  ChartOptions,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Pie, Bar, Line } from "react-chartjs-2";

// ---------- Stable plugin at module scope ----------
const centerTextPlugin = {
  id: "centerText",
  beforeDraw: (chart: any) => {
    if (chart.config.type === "pie") {
      const { ctx, chartArea } = chart;
      const centerX =
        chartArea.left + (chartArea.right - chartArea.left) / 2;
      const centerY =
        chartArea.top + (chartArea.bottom - chartArea.top) / 2;

      const total = chart.data.datasets[0].data.reduce(
        (a: number, b: number) => a + b,
        0
      );

      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = "bold 20px Garamond, serif";
      ctx.fillStyle = "#333";
      ctx.fillText(total.toLocaleString(), centerX, centerY);
      ctx.restore();
    }
  },
};

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  ChartDataLabels,
  centerTextPlugin
);

interface ThreatCategoryData {
  total: number;
  lastMonth: number;
}
interface ActiveInactiveByCategory {
  active: number;
  inactive: number;
}
interface RansomwareThreat {
  threat: string;
  victims: number;
}
interface MonthlyThreat {
  year: number;
  month: number;
  count: number;
}

export default function Dashboard() {
  const { data: user, error } = useGetUserInfo();

  const {
    data: vulnerableCompaniesReport,
    isLoading: vulnerableCompaniesLoading,
    lastUpdated: vulnerableCompaniesLastUpdated,
    refetch: refetchVulnerableCompanies
  } = useVulnerableCompaniesReport({ autoRefresh: true });

  const [newUsersCurrent, setNewUsersCurrent] = useState<number | null>(null);
  const [newUsersLast, setNewUsersLast] = useState<number | null>(null);
  const [threatData, setThreatData] = useState<Record<
    string,
    ThreatCategoryData
  > | null>(null);
  const [activeInactiveAll, setActiveInactiveAll] = useState<{
    active: number;
    inactive: number;
  } | null>(null);
  const [activeInactiveByCategory, setActiveInactiveByCategory] = useState<
    Record<string, ActiveInactiveByCategory> | null
  >(null);

  const [phishingLastActivity, setPhishingLastActivity] = useState<
    Record<string, number> | null
  >(null);
  const [ransomwareTopThreats, setRansomwareTopThreats] = useState<
    RansomwareThreat[] | null
  >(null);
  const [monthlyActiveThreats, setMonthlyActiveThreats] = useState<
    MonthlyThreat[] | null
  >(null);
  const [isRefreshingVulnerableCompanies, setIsRefreshingVulnerableCompanies] = useState(false);

  // ---------- One toast for hook error ----------
  useEffect(() => {
    if (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Unexpected server error. Please try again later."
      );
    }
  }, [error]);

  const handleRefreshVulnerableCompanies = async () => {
    try {
      setIsRefreshingVulnerableCompanies(true);
      await refetchVulnerableCompanies();
      toast.success("Vulnerable companies report refreshed successfully");
    } catch {
      toast.error("Failed to refresh vulnerable companies report");
    } finally {
      setIsRefreshingVulnerableCompanies(false);
    }
  };

  // ---------- Batch all requests so React 18 batches state updates (1 paint) ----------
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [
          newThreatsRes,
          threatCategoriesRes,
          activeAllRes,
          activeByCatRes,
          phishingRes,
          ransomwareRes,
          monthlyRes,
        ] = await Promise.all([
          axios.get("/api/dashboard/new-threats"),
          axios.get("/api/dashboard/threat-categories"),
          axios.get("/api/dashboard/active-inactive-all"),
          axios.get("/api/dashboard/active-inactive-by-category"),
          axios.get("/api/dashboard/phishing-last-activity"),
          axios.get("/api/dashboard/ransomware-top-threats"),
          axios.get("/api/dashboard/monthly-active-threats"),
        ]);

        setNewUsersCurrent(newThreatsRes.data.totalCurrent ?? 0);
        setNewUsersLast(newThreatsRes.data.totalLast ?? 0);
        setThreatData(threatCategoriesRes.data);
        setActiveInactiveAll({
          active: activeAllRes.data.active,
          inactive: activeAllRes.data.inactive,
        });
        setActiveInactiveByCategory(activeByCatRes.data);
        setPhishingLastActivity(phishingRes.data);
        setRansomwareTopThreats(ransomwareRes.data);
        setMonthlyActiveThreats(monthlyRes.data);
      } catch {
        toast.error("Failed to load dashboard data.");
      }
    };
    fetchAll();
  }, []);

  // ---------- Responsive font sizing without recreating chart objects ----------
  const [vw, setVw] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ---------- Static labels/cats (stable refs) ----------
  const categories = useMemo(
    () => ["ransomware", "phishing", "malware", "socialmedia"],
    []
  );
  const labelsFull = useMemo(
    () => ["Ransomware", "Phishing", "Malware", "Social Media"],
    []
  );
  const labelsCompact = useMemo(() => ["RW", "PH", "MW", "SM"], []);

  // ---------- Cards ----------
  const CardInfo = useMemo(
    () => [
      {
        title: "New Users Added (This Month)",
        value: newUsersCurrent ?? null,
        lastMonthValue: newUsersLast ?? null,
        color: "FF3B30",
      },
      {
        title: "Ransomware Threats",
        value: threatData?.["Ransomware Threats"]?.total ?? null,
        lastMonthValue: threatData?.["Ransomware Threats"]?.lastMonth ?? null,
        color: "3EB489",
      },
      {
        title: "Phishing Threats",
        value: threatData?.["Phishing Threats"]?.total ?? null,
        lastMonthValue: threatData?.["Phishing Threats"]?.lastMonth ?? null,
        color: "5856D6",
      },
      {
        title: "Malware Threats",
        value: threatData?.["Malware Threats"]?.total ?? null,
        lastMonthValue: threatData?.["Malware Threats"]?.lastMonth ?? null,
        color: "FF70D2",
      },
      {
        title: "Social Media Threats",
        value: threatData?.["Social Media Threats"]?.total ?? null,
        lastMonthValue: threatData?.["Social Media Threats"]?.lastMonth ?? null,
        color: "FF7300",
      },
    ],
    [newUsersCurrent, newUsersLast, threatData]
  );

  // ---------- Pie: Active vs Inactive (all) ----------
  const pieData = useMemo(
    () => ({
      labels: ["Active Threats", "Inactive Threats"],
      datasets: [
        {
          data: activeInactiveAll
            ? [activeInactiveAll.active, activeInactiveAll.inactive]
            : [0, 0],
          backgroundColor: ["#007bff", "#808080"],
          borderWidth: 0,
        },
      ],
    }),
    [activeInactiveAll]
  );

  const pieOptions: ChartOptions<"pie"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { bottom: 10 } },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            padding: 8,
            font: { size: vw < 768 ? 8 : 10, family: "Garamond, serif" },
            color: "#333",
            boxWidth: 6,
            boxHeight: 6,
          },
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed as number;
              const total = (context.dataset.data as number[]).reduce(
                (a, b) => a + b,
                0
              );
              const percentage = total
                ? ((value / total) * 100).toFixed(1)
                : "0.0";
              return `${context.label}: ${value.toLocaleString()} (${percentage}%)`;
            },
          },
        },
        datalabels: {
          display: true,
          color: "white",
          font: {
            weight: "normal",
            size: vw < 768 ? 8 : 11,
            family: "Garamond, serif",
          },
          formatter: (value: number, ctx: any) =>
            vw < 768
              ? `${value.toLocaleString()}`
              : `${ctx.chart.data.labels?.[ctx.dataIndex]}\n${value.toLocaleString()}`,
          anchor: "center",
          align: "center",
          textAlign: "center",
          clamp: true,
        },
      },
      cutout: "0%",
      elements: { arc: { borderWidth: 0 } },
      animation: activeInactiveAll ? false : undefined, // disable once data present
    }),
    [activeInactiveAll, vw]
  );

  // ---------- Bar: Active vs Inactive by category (100% stacked) ----------
  const barData = useMemo(() => {
    const labels = vw < 768 ? labelsCompact : labelsFull;
    const act = categories.map(
      (cat) => activeInactiveByCategory?.[cat]?.active ?? 0
    );
    const inact = categories.map(
      (cat) => activeInactiveByCategory?.[cat]?.inactive ?? 0
    );
    const total = act.map((v, i) => v + inact[i]);

    return {
      labels,
      datasets: [
        {
          label: "Active Threats",
          data: total.map((t, i) => (t > 0 ? (act[i] / t) * 100 : 0)),
          backgroundColor: "#ff7f35",
        },
        {
          label: "Inactive Threats",
          data: total.map((t, i) => (t > 0 ? (inact[i] / t) * 100 : 0)),
          backgroundColor: "#999999",
        },
      ],
    };
  }, [activeInactiveByCategory, categories, labelsCompact, labelsFull, vw]);

  const barOptions: ChartOptions<"bar"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "bottom" },
        tooltip: {
          callbacks: {
            label: (context) => `${context.formattedValue}%`,
          },
        },
        datalabels: {
          display: true,
          color: "white",
          font: { size: vw < 768 ? 6 : 8 },
          formatter: (value: number) => `${value.toFixed(1)}%`,
          anchor: "center",
          align: "center",
        },
      },
      scales: {
        x: { stacked: true, grid: { display: false } },
        y: {
          stacked: true,
          min: 0,
          max: 100,
          ticks: { callback: (value) => `${value}%` },
          grid: { display: true },
        },
      },
      animation: activeInactiveByCategory ? false : undefined,
    }),
    [activeInactiveByCategory, vw]
  );

  // ---------- Pie: Phishing last activity ----------
  const phishingPieData = useMemo(
    () => ({
      labels: phishingLastActivity ? Object.keys(phishingLastActivity) : [],
      datasets: [
        {
          data: phishingLastActivity ? Object.values(phishingLastActivity) : [],
          backgroundColor: ["#8979ff", "#ff928a", "#3cc3df", "#ffae4d"],
          borderWidth: 0,
        },
      ],
    }),
    [phishingLastActivity]
  );

  const phishingPieOptions: ChartOptions<"pie"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { bottom: 20 } },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            padding: 8,
            font: { size: vw < 768 ? 8 : 10, family: "Garamond, serif" },
            color: "#333",
            boxWidth: 4,
            boxHeight: 4,
          },
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed as number;
              const total = (context.dataset.data as number[]).reduce(
                (a, b) => a + b,
                0
              );
              const percentage = total
                ? ((value / total) * 100).toFixed(1)
                : "0.0";
              return `${context.label}: ${value.toLocaleString()} (${percentage}%)`;
            },
          },
        },
        datalabels: {
          display: true,
          color: "white",
          font: {
            weight: "bold",
            size: vw < 768 ? 8 : 11,
            family: "Garamond, serif",
          },
          formatter: (value: number, context: any) => {
            const total = (context.dataset.data as number[]).reduce(
              (a, b) => a + b,
              0
            );
            const percentage = total
              ? ((value / total) * 100).toFixed(1)
              : "0.0";
            return vw < 768
              ? `${value.toLocaleString()}`
              : `${value.toLocaleString()}\n${percentage}%`;
          },
          anchor: "center",
          align: "center",
          textAlign: "center",
          clamp: true,
        },
      },
      cutout: "50%",
      elements: { arc: { borderWidth: 0 } },
      animation: phishingLastActivity ? false : undefined,
    }),
    [phishingLastActivity, vw]
  );

  // ---------- Bar: Ransomware Top 5 ----------
  const ransomwareBarData = useMemo(
    () => ({
      labels: ransomwareTopThreats
        ? ransomwareTopThreats.map((item) => item.threat)
        : [],
      datasets: [
        {
          label: "Victims",
          data: ransomwareTopThreats
            ? ransomwareTopThreats.map((item) => item.victims)
            : [],
          backgroundColor: ["#66c3a1", "#b2e0d0", "#b2e0d0", "#b2e0d0", "#b2e0d0"],
          borderWidth: 0,
        },
      ],
    }),
    [ransomwareTopThreats]
  );

  const ransomwareBarOptions: ChartOptions<"bar"> = useMemo(
    () => ({
      indexAxis: "y",
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleColor: "#fff",
          bodyColor: "#fff",
          cornerRadius: 6,
        },
        datalabels: {
          display: true,
          anchor: "end",
          align: "start",
          color: "#333",
          font: {
            weight: "bold",
            size: vw < 768 ? 8 : 12,
            family: "Garamond, sans-serif",
          },
          formatter: (value: number) => value.toLocaleString(),
          offset: -1,
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: 10 } },
      scales: {
        x: { beginAtZero: true, display: false, grid: { display: false } },
        y: {
          ticks: {
            autoSkip: false,
            font: { size: vw < 768 ? 8 : 11, family: "Garamond, sans-serif" },
            color: "#333",
          },
          grid: { display: false },
        },
      },
      elements: { bar: { borderRadius: 4 } },
      animation: ransomwareTopThreats ? false : undefined,
    }),
    [ransomwareTopThreats, vw]
  );

  // ---------- Line: Monthly Active Threats ----------
  const monthlyLabels = useMemo(
    () =>
      monthlyActiveThreats
        ? monthlyActiveThreats.map(
            (item) => `${item.year}-${item.month.toString().padStart(2, "0")}`
          )
        : [],
    [monthlyActiveThreats]
  );

  const monthlyLineData = useMemo(
    () => ({
      labels: monthlyLabels,
      datasets: [
        {
          label: "Monthly Active Threats",
          data: monthlyActiveThreats
            ? monthlyActiveThreats.map((item) => item.count)
            : [],
          fill: false,
          borderColor: "#b6abfb",
          tension: 0.1,
        },
      ],
    }),
    [monthlyLabels, monthlyActiveThreats]
  );

  const monthlyLineOptions: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleColor: "#fff",
          bodyColor: "#fff",
          cornerRadius: 6,
        },
        datalabels: {
          display: vw >= 768,
          anchor: "end",
          align: "left",
          color: "#666",
          font: {
            size: vw < 768 ? 8 : 10,
            family: "Garamond, serif",
            weight: "normal",
          },
          formatter: (value: number) => value.toLocaleString(),
          offset: 4,
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            font: { size: vw < 768 ? 8 : 10, family: "Garamond, serif" },
            color: "#666",
            maxTicksLimit: vw < 768 ? 4 : 12,
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(0, 0, 0, 0.1)" },
          ticks: {
            font: { size: vw < 768 ? 8 : 10, family: "Garamond, serif" },
            color: "#666",
          },
        },
      },
      elements: {
        line: { tension: 0.4, borderWidth: 1 },
        point: {
          radius: vw < 768 ? 1 : 2,
          hoverRadius: vw < 768 ? 2 : 3,
          borderColor: "#fff",
          borderWidth: 1,
        },
      },
      animation: monthlyActiveThreats ? false : undefined,
    }),
    [monthlyActiveThreats, vw]
  );

  // ---------- Reusable chart card ----------
  const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({
    title,
    children,
  }) => (
    <div className="bg-white rounded-md p-2 sm:p-4 lg:p-6 shadow-sm flex flex-col">
      <h2 className="text-xs sm:text-sm lg:text-base font-bold mb-2 sm:mb-3 lg:mb-4 text-gray-800">
        {title}
      </h2>
      <div className="flex-1">
        <div className="relative w-full h-48 sm:h-56 md:h-64 lg:h-72">
          <div className="absolute inset-0">{children}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-[#F5F5F5] min-h-screen w-full p-2 sm:p-4 lg:p-5 overflow-auto mt-16 sm:mt-20">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-base sm:text-lg lg:text-xl font-bold">
          Hello, {user?.username || "User"}
        </h1>
        <p className="px-2 sm:px-6 lg:px-14 pt-2 sm:pt-4 text-xs sm:text-sm text-[#757575] leading-relaxed">
          This dashboard summarises the latest threat intelligence gathered by
          the Excelitte web crawler. View key statistics, monitor trends, and
          quickly jump to essential features—like the Chatbot (for in-depth
          threat queries) or Profile page (to manage your account details).
        </p>

        {/* Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 lg:gap-6 mt-3 sm:mt-4 px-2 sm:px-6">
          {CardInfo.map((e, index) => (
            <CardElement
              key={index}
              title={e.title}
              value={e.value}
              lastMonthValue={e.lastMonthValue}
              color={e.color}
            />
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="max-w-7xl mx-auto mt-4 sm:mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
          <ChartCard title="Active vs. Inactive Threats (All Categories) - Overall threat status distribution">
            {activeInactiveAll ? (
              <Pie data={pieData} options={pieOptions} redraw={false} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-xs sm:text-sm text-gray-500">
                  Loading chart...
                </div>
              </div>
            )}
          </ChartCard>

          <ChartCard title="Active vs. Inactive Threats - Percentage breakdown by threat type">
            {activeInactiveByCategory ? (
              <Bar data={barData} options={barOptions} redraw={false} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-xs sm:text-sm text-gray-500">
                  Loading chart...
                </div>
              </div>
            )}
          </ChartCard>

          <ChartCard title="Top 5 Common Ransomware Threats - Most frequent ransomware families detected">
            {ransomwareTopThreats ? (
              <Bar
                data={ransomwareBarData}
                options={ransomwareBarOptions}
                redraw={false}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-xs sm:text-sm text-gray-500">
                  Loading chart...
                </div>
              </div>
            )}
          </ChartCard>

          <ChartCard title="Phishing Last Activity - Recent phishing campaign activity timeline">
            {phishingLastActivity ? (
              <Pie
                data={phishingPieData}
                options={phishingPieOptions}
                redraw={false}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-xs sm:text-sm text-gray-500">
                  Loading chart...
                </div>
              </div>
            )}
          </ChartCard>

          <ChartCard title="Monthly Active Threats (Past Year) - Threat volume trends over time">
            {monthlyActiveThreats ? (
              <Line
                data={monthlyLineData}
                options={monthlyLineOptions}
                redraw={false}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-xs sm:text-sm text-gray-500">
                  Loading chart...
                </div>
              </div>
            )}
          </ChartCard>

          <QuickLinks />
        </div>

        {/* AI-Powered Threat Intelligence Panel */}
        <div className="mt-4 sm:mt-6">
          <VulnerableCompaniesTable
            report={vulnerableCompaniesReport}
            isLoading={vulnerableCompaniesLoading}
            lastUpdated={vulnerableCompaniesLastUpdated}
            onRefresh={handleRefreshVulnerableCompanies}
            isRefreshing={isRefreshingVulnerableCompanies}
          />
        </div>

        {/* Bottom padding */}
        <div className="pb-6 sm:pb-10 lg:pb-20"></div>
      </div>
    </div>
  );
}
